import * as Sentry from '@sentry/react';
import { LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BankLogoWithDescription } from 'src/components/BankLogoWithDescription';

import { cn } from '~/utils/cnTailwind.ts';
import { downloadFile } from '~/utils/downloadFile';
import { openFileInNewTab } from '~/utils/openFileInNewTab';

type Props = {
    bankLogo?: string | null;
    bankName?: string | null;
    description: string | null | undefined;
    aggregateId?: string;
    offerId?: string;
    enableOpenOffer?: boolean;
    manualOfferDocumentKey?: string;
    acceptedOffer?: SchemaLoanRequestResolver_GetAcceptedOffers[number];
};

export const BankLogo: React.FC<Props> = ({
    acceptedOffer,
    aggregateId,
    bankLogo,
    bankName,
    description,
    enableOpenOffer,
    manualOfferDocumentKey,
    offerId,
}) => {
    const { t } = useTranslation();
    const handleOpenPdf = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation(); // needed to prevent opening detailed view for manual offer
        if (!aggregateId || !offerId) return;

        const url = manualOfferDocumentKey
            ? `/offer?aggregateId=${aggregateId}&key=${manualOfferDocumentKey}`
            : `/offer/download?aggregateId=${aggregateId}&offerId=${offerId}`;

        try {
            await openFileInNewTab({
                mimeType: 'application/pdf',
                route: url,
            });
        } catch (error) {
            toast.error(t('OFFER_MANAGEMENT.TOASTS.FAILED_TO_OPEN_PDF'));
            Sentry.captureException(error);
        }
    };
    const downloadEuropaceZip = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (acceptedOffer?.source !== LOAN_REQUEST_OFFER_SOURCES.EUROPACE) return;
        e.stopPropagation();
        if (!aggregateId || !offerId) return;
        try {
            await downloadFile({
                mimeType: 'application/zip',
                name: `Angebot-${acceptedOffer.id}${acceptedOffer.bank ? `-${acceptedOffer.bank}` : ''}.zip`,
                route: `/offer/download?aggregateId=${aggregateId}&offerId=${offerId}`,
            });
        } catch (error) {
            // eslint-disable-next-line no-restricted-syntax
            for (const document of acceptedOffer?.documents ?? []) {
                const newWindow = window.open(document.url, '_blank', 'noopener,noreferrer');
                if (newWindow) newWindow.opener = null; // Remove the reference to the current window to prevent tabnabbing, phishing attacks.
            }
        }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (
            acceptedOffer?.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE &&
            acceptedOffer?.documents &&
            acceptedOffer?.documents.length > 1
        ) {
            downloadEuropaceZip(e);
        } else {
            handleOpenPdf(e);
        }
    };

    const logoContent = (
        <BankLogoWithDescription
            description={description}
            bankLogo={bankLogo}
            bankName={bankName}
            imgClassName={cn(
                'w-[85px] min-w-[85px] max-h-[25px]',
                bankLogo && bankLogo.includes('consorsFinanz') ? 'w-5 min-w-5' : '',
            )}
        />
    );

    return (
        <td className="text-10 text-center text-grey whitespace-nowrap p-2">
            {enableOpenOffer ? (
                <button type="button" onClick={handleClick} className="cursor-pointer" data-testid="bank-logo-button">
                    {logoContent}
                </button>
            ) : (
                logoContent
            )}
        </td>
    );
};
